// Locale primitives and URL helpers for the multilingual SPA.
//
// English is the default locale and lives at the unprefixed root (`/`,
// `/r/<slug>`, `/properties`, etc). All other locales are prefixed with the
// locale code (`/fr`, `/fr/r/<slug>`, `/fr/properties`, etc).
//
// IMPORTANT: slugs are kept identical across locales (the canonical English
// slug). This eliminates slug-stability risk and is a common pattern on
// large multilingual SEO sites. Hreflang still gives full SEO coverage.

export const DEFAULT_LOCALE = "en" as const;

export const LOCALES = ["en", "fr", "es", "de", "nl", "ru"] as const;

export type Locale = (typeof LOCALES)[number];

export const NON_DEFAULT_LOCALES: ReadonlyArray<Exclude<Locale, "en">> = [
  "fr",
  "es",
  "de",
  "nl",
  "ru",
];

export const LOCALE_LABELS: Record<Locale, string> = {
  en: "English",
  fr: "Français",
  es: "Español",
  de: "Deutsch",
  nl: "Nederlands",
  ru: "Русский",
};

// BCP 47 language tags used in <html lang>, hreflang, and Intl formatters.
export const LOCALE_BCP47: Record<Locale, string> = {
  en: "en",
  fr: "fr",
  es: "es",
  de: "de",
  nl: "nl",
  ru: "ru",
};

// Intl locale used for currency / number / date formatting. We always
// format MAD prices, but the surrounding number grouping changes per locale.
export const LOCALE_INTL: Record<Locale, string> = {
  en: "en-MA",
  fr: "fr-FR",
  es: "es-ES",
  de: "de-DE",
  nl: "nl-NL",
  ru: "ru-RU",
};

/**
 * ISO 4217 currency code to display for each locale.
 * en → USD, European locales → EUR, ru → RUB.
 */
export const LOCALE_CURRENCY: Record<Locale, string> = {
  en: "USD",
  fr: "EUR",
  es: "EUR",
  de: "EUR",
  nl: "EUR",
  ru: "RUB",
};

/**
 * Static MAD → target-currency conversion rates.
 * Last updated: 2025-05. Update whenever rates drift > 5 %.
 *   Source: xe.com / open exchange rates snapshot
 */
export const MAD_RATE: Record<string, number> = {
  USD: 0.1002,
  EUR: 0.0920,
  RUB: 9.19,
  MAD: 1,
};

const LOCALE_SET: ReadonlySet<string> = new Set(LOCALES);

/**
 * localStorage key used to persist the visitor's locale preference so the
 * auto-redirect only fires on the very first visit (or after a manual switch).
 */
export const LOCALE_PREF_KEY = "riadata_locale_pref";

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && LOCALE_SET.has(value);
}

/**
 * Detect the visitor's preferred locale from the browser's language list.
 * Returns the first supported non-English locale found in `navigator.languages`
 * (falling back to `navigator.language`), or `null` when no match exists.
 *
 * Only runs in the browser — returns null during SSR.
 */
export function getBrowserLocale(): Exclude<Locale, "en"> | null {
  if (typeof navigator === "undefined") return null;
  const langs: readonly string[] =
    navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const lang of langs) {
    const code = lang.split("-")[0].toLowerCase();
    if ((NON_DEFAULT_LOCALES as ReadonlyArray<string>).includes(code)) {
      return code as Exclude<Locale, "en">;
    }
  }
  return null;
}

/**
 * Strip a leading "/<locale>" segment from `path` and return the locale plus
 * the remainder. If `path` does not start with a known non-default locale,
 * the locale is "en" and the original path is returned untouched.
 */
export function parseLocaleFromPath(path: string): {
  locale: Locale;
  rest: string;
} {
  if (!path || path === "/") return { locale: DEFAULT_LOCALE, rest: "/" };
  // Tolerate base-path stripping done by the SSR layer: ensure we always
  // operate on a string that starts with "/".
  const normalised = path.startsWith("/") ? path : `/${path}`;
  const m = normalised.match(/^\/([a-z]{2})(?=\/|$)/);
  if (m && isLocale(m[1]) && m[1] !== DEFAULT_LOCALE) {
    const rest = normalised.slice(m[0].length);
    return { locale: m[1], rest: rest === "" ? "/" : rest };
  }
  return { locale: DEFAULT_LOCALE, rest: normalised };
}

/**
 * Build a locale-prefixed application path. English returns the path
 * unchanged; other locales get the "/<locale>" prefix.
 *
 *   withLocale("en", "/r/foo") -> "/r/foo"
 *   withLocale("fr", "/r/foo") -> "/fr/r/foo"
 *   withLocale("fr", "/")      -> "/fr"
 */
export function withLocale(locale: Locale, path: string): string {
  const clean = path.startsWith("/") ? path : `/${path}`;
  if (locale === DEFAULT_LOCALE) return clean;
  if (clean === "/") return `/${locale}`;
  return `/${locale}${clean}`;
}

/**
 * Replace the locale segment in a path while preserving the rest. Used by
 * the language switcher to produce the "same page in another locale" URL.
 */
export function swapLocaleInPath(path: string, target: Locale): string {
  const { rest } = parseLocaleFromPath(path);
  return withLocale(target, rest);
}
